import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { auth } from '../services/firebaseConfig';
import { onAuthStateChanged, signOut, signInAnonymously, getIdToken } from 'firebase/auth';
import { ensureUserDoc, invalidateUserMetaCache } from '../services/firebaseService';
import { stopAppReadyTime } from '../utilities/perfTraces';
import { isNativeWebView } from '../nativeBridge.ts';

const AuthContext = createContext();

// Auth intent management via localStorage
const AUTH_INTENT_KEY = 'tidle_auth_intent';
const AUTH_INTENT_GUEST = 'guest';
const AUTH_INTENT_LOGIN = 'login';

const getAuthIntent = () => {
  try {
    return localStorage.getItem(AUTH_INTENT_KEY) || AUTH_INTENT_GUEST;
  } catch (e) {
    return AUTH_INTENT_GUEST; // Default to guest if localStorage unavailable
  }
};

const setAuthIntent = (intent) => {
  try {
    localStorage.setItem(AUTH_INTENT_KEY, intent);
  } catch (e) {
    console.warn('Failed to set auth intent in localStorage:', e);
  }
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);
  const hasAttemptedAnonSignIn = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User exists (either authenticated or anonymous)
        if (isMounted) {
          setCurrentUser(user);
          setLoading(false);
          setAuthReady(true);
          // Stop app_ready_time trace when auth is ready
          stopAppReadyTime();
        }
        
        // Ensure user doc exists in Firestore with profile data (this also populates cache)
        ensureUserDoc(user.uid, {
          accountType: user.isAnonymous ? "guest" : "registered",
        });
        
        // Clear intent back to "guest" when a non-anonymous user is present
        if (!user.isAnonymous) {
          setAuthIntent(AUTH_INTENT_GUEST);
        }
        // When an anonymous user is present and intent == "login", keep intent == "login"
        // (do not auto-clear) - this allows them to sign in without auto-creating new guest
        
        // Reset flag when we have a user so we can sign in anonymously again if needed
        hasAttemptedAnonSignIn.current = false;
      } else {
        // User logged out - invalidate cache (optimization #2)
        invalidateUserMetaCache();
        // No user - check auth intent before auto-signing in
        const intent = getAuthIntent();
        
        if (intent === AUTH_INTENT_LOGIN) {
          // User explicitly logged out - DO NOT create anonymous user
          if (isMounted) {
            setCurrentUser(null);
            setLoading(false);
            setAuthReady(true);
            // Stop app_ready_time trace when auth is ready (even if no user)
            stopAppReadyTime();
          }
        } else {
          // Native iOS/Android app (WebView): auto sign-in anonymously and redirect to /main (no landing)
          if (isNativeWebView()) {
            signInAnonymously(auth)
              .then(() => {
                // onAuthStateChanged will fire with the new user and set state
              })
              .catch((err) => {
                console.warn('Native app anonymous sign-in failed', err);
                if (isMounted) {
                  setCurrentUser(null);
                  setLoading(false);
                  setAuthReady(true);
                  stopAppReadyTime();
                }
              });
            return;
          }
          // Default "guest" intent - for first-time visitors, don't auto-sign-in
          // Let them see the landing page first, then sign in anonymously when they interact
          if (isMounted) {
            setCurrentUser(null);
            setLoading(false);
            setAuthReady(true);
            stopAppReadyTime();
          }
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe(); // Cleanup subscription on unmount
    };
  }, []);

  // Tidle Desktop (Electron): send idToken + projectId to main process so native menu bar helper can sign in
  // projectId must match the Firebase project the token was issued for (dev vs prod)
  useEffect(() => {
    if (!currentUser?.uid || currentUser.isAnonymous) return;
    const sendToken = window.electron?.sendAuthToken;
    if (typeof sendToken !== 'function') return;
    const projectId = auth.app?.options?.projectId || null;
    let cancelled = false;
    const send = () => {
      getIdToken(currentUser, false)
        .then((idToken) => {
          if (!cancelled && idToken) sendToken(idToken, projectId);
        })
        .catch(() => {});
    };
    send();
    // Re-send after a short delay so native helper sees token even if first send was slow or panel opened early
    const t = setTimeout(send, 1500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [currentUser?.uid, currentUser?.isAnonymous]);

  // Logout function
  const logout = async () => {
    try {
      // Tidle Desktop: clear native menu helper auth file so it shows "Open Tidle to sign in"
      if (typeof window !== 'undefined' && window.electron?.clearAuth) {
        window.electron.clearAuth().catch(() => {});
      }
      // Set intent to "login" BEFORE signing out to prevent auto-anonymous sign-in
      setAuthIntent(AUTH_INTENT_LOGIN);
      // Invalidate user metadata cache on logout (optimization #2)
      invalidateUserMetaCache();
      await signOut(auth); // Sign out the user using Firebase's signOut method
      // After sign out, onAuthStateChanged will fire with null user,
      // but intent == "login" will prevent anonymous sign-in
      setCurrentUser(null); // Clear the current user state temporarily
    } catch (error) {
      console.error("Failed to log out", error);
      throw error; // Re-throw the error for handling in the component
    }
  };

  // Derive isGuest from currentUser
  const isGuest = currentUser?.isAnonymous ?? false;
  
  // Get current auth intent for routing decisions
  const authIntent = getAuthIntent();

  // Always render children; loading/authReady stay in context for consumers (e.g. RedirectPage)
  return (
    <AuthContext.Provider value={{ currentUser, loading, authReady, isGuest, authIntent, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
